
import { Component, Watch, Mixins } from 'vue-property-decorator';
import ResponsivenessMixin from '@/features/common/mixins/responsivenessMixin';
import ChangesSaved from '@/features/settings/components/ChangesSaved.vue';
import {
  getUserNotificationsPrefs,
  updateNotificationPref,
} from '@/features/settings/services/notifications.services';
import { NotificationPrefType } from '@/features/settings/types';
import { isLocalhost } from '@/features/common/helpers';

interface SlideConfig {
  titleKey: string;
  mp4: string;
}

@Component({
  components: {
    ChangesSaved,
  },
})
export default class UpdatesPreferences extends Mixins(ResponsivenessMixin) {
  private formData = {
    daily: {
      show: false,
      name: 'DailyEmailSummary',
    },
    weekly: {
      show: false,
      name: 'WeeklyEmailSummary',
    },
    monthly: {
      show: false,
      name: 'MonthlyEmailSummary',
    },
  }

  private saving = false;

  private showAlert = {
    success: false,
    show: false,
  }

  private activeSlide = 0;

  // eslint-disable-next-line class-methods-use-this
  private get staticBaseUrl(): string {
    return isLocalhost() ? 'https://test.secureship.ca' : '';
  }

  private get slides(): SlideConfig[] {
    const base = this.staticBaseUrl;
    return [
      {
        titleKey: 'preferences.updates.dailyEmailExample',
        mp4: `${base}/videos/email-previews/daily.mp4`,
      },
      {
        titleKey: 'preferences.updates.weeklyEmailExample',
        mp4: `${base}/videos/email-previews/weekly.mp4`,
      },
      {
        titleKey: 'preferences.updates.monthlyEmailExample',
        mp4: `${base}/videos/email-previews/monthly.mp4`,
      },
    ];
  }

  get carouselTitle(): string {
    return this.$t(this.slides[this.activeSlide].titleKey) as string;
  }

  get isTablet(): boolean {
    return this.$vuetify.breakpoint.lgAndDown;
  }

  get isNotificationPrefs(): boolean {
    return this.$store.getters.getIsNotificationPrefs;
  }

  get notificationPrefs(): NotificationPrefType[] {
    return this.$store.getters.getNotificationPrefs;
  }

  async created(): Promise<void> {
    if (!this.isNotificationPrefs) {
      const notificationPrefs = await getUserNotificationsPrefs();
      this.$store.commit('setNotificationPrefs', notificationPrefs);
    }
  }

  @Watch('isNotificationPrefs', { immediate: true })
  private populatePreferences(newValue: boolean) {
    if (newValue) {
      this.formData.daily.show = this.searchNotificationPref(
        this.formData.daily.name,
      );
      this.formData.weekly.show = this.searchNotificationPref(
        this.formData.weekly.name,
      );
      this.formData.monthly.show = this.searchNotificationPref(
        this.formData.monthly.name,
      );
    }
  }

  private searchNotificationPref(name: string): boolean {
    const notification = this.notificationPrefs.find((pref) => pref.name === name);
    if (!notification) return false;
    return notification.show;
  }

  private prev(): void {
    this.activeSlide = this.activeSlide === 0
      ? this.slides.length - 1
      : this.activeSlide - 1;
  }

  private next(): void {
    this.activeSlide = this.activeSlide === this.slides.length - 1
      ? 0
      : this.activeSlide + 1;
  }

  private async savePreferences(): Promise<void> {
    this.saving = true;

    const entries = [this.formData.daily, this.formData.weekly, this.formData.monthly];

    // Sequential calls to avoid backend race condition (last-write-wins)
    const allSucceeded = await entries.reduce(
      async (prevPromise, entry) => {
        const prevOk = await prevPromise;
        if (!prevOk) return false;

        const result = await updateNotificationPref(entry.name, entry.show);
        if (!result) return false;

        this.$store.commit('patchNotificationPref', {
          name: entry.name,
          value: entry.show,
        });
        return true;
      },
      Promise.resolve(true),
    );

    if (!allSucceeded) {
      this.formData.daily.show = this.searchNotificationPref(this.formData.daily.name);
      this.formData.weekly.show = this.searchNotificationPref(this.formData.weekly.name);
      this.formData.monthly.show = this.searchNotificationPref(this.formData.monthly.name);
    }

    this.saving = false;

    this.showAlert = {
      show: true,
      success: allSucceeded,
    };
  }
}
